import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AngularFireDatabase, AngularFireList } from "@angular/fire/database";
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from "@angular/fire/firestore";
import { AngularFireStorage } from "@angular/fire/storage";

import { Observable } from "rxjs";
import { finalize, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";
import { FileStorage } from "../models/file-storage.model";
import { UUID } from "angular2-uuid";

@Injectable({
  providedIn: "root",
})
export class FileStorageService {
  baseUrl = environment.apiUrl;
  private dbPath = "/uploads";
  private basePath = "/users/uploads";
  collRef: AngularFirestoreCollection<FileStorage>;
  userLogin: User;

  constructor(
    private http: HttpClient,
    private db: AngularFirestore,
    private storage: AngularFireStorage,
    private userStore: UserService
  ) {
    this.collRef = db.collection(this.dbPath);

    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  pushFileToStorage(fileUpload: FileStorage): Observable<number | undefined> {
    const dateNow = new Date().getTime();
    const fileId = UUID.UUID();

    const fileKey = `${this.userLogin.uid}_${dateNow}_${fileId}`;
    const filePath = `${this.basePath}/${fileKey}`;
    const storageRef = this.storage.ref(filePath);
    const uploadTask = this.storage.upload(filePath, fileUpload.file);

    uploadTask
      .snapshotChanges()
      .pipe(
        finalize(() => {
          storageRef.getDownloadURL().subscribe((downloadURL) => {
            console.log("FileStorageService/downloadURL:", downloadURL);

            fileUpload.key = fileKey;
            fileUpload.url = downloadURL;
            fileUpload.name = fileUpload.file.name;
            fileUpload.type = fileUpload.file.type;
            fileUpload.path = filePath;
            fileUpload.source = "firebase";
            this.saveFileData(fileUpload);
          });
        })
      )
      .subscribe();

    return uploadTask.percentageChanges();
  }

  saveFileData(fileUpload: FileStorage) {
    fileUpload.key = fileUpload.key ? fileUpload.key : "";
    fileUpload.name = fileUpload.name ? fileUpload.name : "";
    fileUpload.url = fileUpload.url ? fileUpload.url : "";
    fileUpload.type = fileUpload.type ? fileUpload.type : "file";
    fileUpload.path = fileUpload.path ? fileUpload.path : "";
    fileUpload.source = fileUpload.source ? fileUpload.source : "unknown";

    fileUpload.isActive = true;
    fileUpload.isDeleted = false;
    fileUpload.createdOn = new Date();
    fileUpload.createdBy = this.userLogin.uid;
    fileUpload.updatedOn = new Date();
    fileUpload.updatedBy = this.userLogin.uid;

    delete fileUpload.file;
    console.log("saveFileData:", fileUpload);

    return this.collRef.add({ ...fileUpload }).then((docRef) => {
      fileUpload.id = docRef.id;
      return fileUpload;
    });
  }

  getFiles(uid: string, type: string): AngularFirestoreCollection<FileStorage> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", uid)
        .where("type", "==", type)
        .orderBy("createdOn", "desc")
        .limit(10)
    );
  }

  getMoreFiles(
    uid: string,
    type: string,
    limit?: number,
    cursor?: any
  ): AngularFirestoreCollection<FileStorage> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", uid)
        .where("type", "==", type)
        .orderBy("createdOn", "desc")
        .startAfter(cursor)
        .limit(limit)
    );
  }

  deleteFile(fileUpload: FileStorage): void {
    this.deleteFileDatabase(fileUpload.key)
      .then(() => {
        this.deleteFileStorage(fileUpload.name);
      })
      .catch((error) => console.log(error));
  }

  private deleteFileDatabase(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.collRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  private deleteFileStorage(name: string): void {
    const storageRef = this.storage.ref(this.dbPath);
    storageRef.child(name).delete();
  }

  //Upload images to AmazonS3
  uploadImageS3(formData: any) {
    return this.http
      .post<FileStorage>(this.baseUrl + "/FileStorage", formData)
      .pipe(
        map((response) => {
          const fileUpload: FileStorage = new FileStorage();
          //fileUpload.key = fileKey;
          fileUpload.url = response.url;
          fileUpload.name = response.name;
          fileUpload.size = formData.data.size;
          fileUpload.type = "image";
          fileUpload.path = response.path;
          fileUpload.source = "amazons3";
          this.saveFileData(fileUpload);

          return response.url;
        })
      );
  }

  //Upload images to ImageKit.io
  async uploadImageKit(formData: any) {
    let tokenData = await this.http
      .get<any>(environment.imageKit.authenticationEndpoint)
      .toPromise();
    formData.append("publicKey", environment.imageKit.publicKey);
    formData.append("signature", tokenData.signature || "");
    formData.append("expire", tokenData.expire || 0);
    formData.append("token", tokenData.token);

    return this.http.post<any>(environment.imageKit.urlServer, formData).pipe(
      map((response) => {
        const fileUpload: FileStorage = new FileStorage();
        fileUpload.key = response.fileId;
        fileUpload.name = response.name;
        fileUpload.url = response.url;
        fileUpload.thumbnailUrl = response.thumbnailUrl;
        fileUpload.size = response.size;
        fileUpload.type = "image";
        fileUpload.path = response.filePath;
        fileUpload.source = "imagekit";
        this.saveFileData(fileUpload);

        return response.url;
      })
    );
  }
}
